import { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Printer,
  RotateCcw,
  Cloud,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';
import { useReportPrint } from '@/hooks/useReportPrint';
import { useRegisterSettlement } from '@/hooks/useRegisterSettlement';
import { printSettlementReport } from '@/components/settlement/SettlementPrintView';
import { toast } from 'sonner';

interface CashDrawerSession {
  id: string;
  location_id: string;
  user_id: string | null;
  register_id: string | null;
  closure_number: number | null;
  opening_balance: number;
  closing_balance: number | null;
  expected_balance: number | null;
  variance: number | null;
  cash_payments: number;
  cash_refunds: number;
  pay_ins: number;
  pay_outs: number;
  status: 'open' | 'closed';
  session_type?: 'register' | 'virtual';
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
  denominations: Record<string, number>;
  payment_summary: Record<string, { expected: number; counted: number }>;
  payment_breakdown?: Record<string, any>;
  items_sold_summary: Array<{ name: string; quantity: number; total: number }>;
  // Settlement tracking
  settled_by?: string | null;
  settled_at?: string | null;
  // Joined data
  register?: { name: string } | null;
  user?: { first_name: string | null; last_name: string | null } | null;
  location?: { name: string } | null;
  settled_by_user?: { first_name: string | null; last_name: string | null } | null;
}

export default function RegisterClosures() {
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { organization, profile } = useAuth();
  const { formatCurrency } = useCurrency();
  const { fetchSettlementData } = useRegisterSettlement();

  const [selectedRegister, setSelectedRegister] = useState<string>('all');
  const [selectedOutlet, setSelectedOutlet] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  const { printRef, handlePrint } = useReportPrint({ title: 'Register closures' });

  // Navigate to view settlement details
  const handleViewSettlement = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row toggle
    navigate(`/settle-register/${sessionId}?view=true`);
  };

  // Handle reprint settlement report
  const handleReprintSettlement = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row toggle
    setReprintingId(sessionId);
    
    try {
      const settlementData = await fetchSettlementData(sessionId);
      
      if (!settlementData) {
        toast.error('Reprint Failed', {
        description: 'Could not load settlement data for this session',
      });
        return;
      }

      // Get the settled by name from the settlement data or current user as fallback
      const settledByName = profile 
        ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() 
        : 'Unknown';

      printSettlementReport({
        data: settlementData,
        settledByName,
        formatCurrency,
      });

      toast.success('Settlement Report Reprinted', {
        description: 'The settlement report has been sent to the printer',
      });
    } catch (error) {
      console.error('Failed to reprint settlement:', error);
      toast.error('Reprint Failed', {
        description: 'An error occurred while reprinting the settlement report',
      });
    } finally {
      setReprintingId(null);
    }
  };

  // Fetch registers
  const { data: registers } = useQuery({
    queryKey: ['registers', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase
        .from('registers')
        .select('id, name')
        .eq('location_id', currentLocation.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!currentLocation?.id,
  });

  // Fetch locations for filtering
  const { data: locations } = useQuery({
    queryKey: ['all-locations', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from('locations')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!organization?.id,
  });

  // Fetch closures
  const { data: closures, isLoading } = useQuery({
    queryKey: ['register-closures', selectedOutlet, selectedRegister],
    queryFn: async () => {
      let query = supabase
        .from('cash_drawer_sessions')
        .select(`
          *,
          register:registers(name),
          user:profiles!cash_drawer_sessions_user_id_fkey(first_name, last_name),
          location:locations(name),
          settled_by_user:profiles!cash_drawer_sessions_settled_by_fkey(first_name, last_name)
        `)
        .order('opened_at', { ascending: false })
        .limit(100);

      if (selectedOutlet !== 'all') {
        query = query.eq('location_id', selectedOutlet);
      } else if (organization?.id) {
        // Get all locations for org
        const { data: orgLocations } = await supabase
          .from('locations')
          .select('id')
          .eq('organization_id', organization.id);

        if (orgLocations) {
          query = query.in('location_id', orgLocations.map(l => l.id));
        }
      }

      if (selectedRegister !== 'all') {
        query = query.eq('register_id', selectedRegister);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as CashDrawerSession[];
    },
    enabled: !!organization?.id,
  });

  // Toggle row expansion
  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!closures || closures.length === 0) return;

    const headers = [
      'Register',
      'Closure #',
      'Outlet',
      'Time Opened',
      'Time Closed',
      'Opening Balance',
      'Expected Balance',
      'Counted Balance',
      'Variance',
      'Cash Payments',
      'Cashier',
    ];

    const rows = closures.map(c => [
      c.register?.name || 'Unknown',
      c.closure_number || '-',
      c.location?.name || '-',
      new Date(c.opened_at).toLocaleString(),
      c.closed_at ? new Date(c.closed_at).toLocaleString() : 'Open',
      c.opening_balance.toFixed(2),
      c.expected_balance?.toFixed(2) || '-',
      c.closing_balance?.toFixed(2) || '-',
      c.variance?.toFixed(2) || '-',
      c.cash_payments.toFixed(2),
      `${c.user?.first_name || ''} ${c.user?.last_name || ''}`.trim() || 'Unknown',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `register-closures-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          View and manage cash register sessions
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Register Filter */}
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground">Register</label>
              <Select value={selectedRegister} onValueChange={setSelectedRegister}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Registers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Registers</SelectItem>
                  {registers?.map((reg) => (
                    <SelectItem key={reg.id} value={reg.id}>
                      {reg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Outlet Filter */}
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground">Outlet</label>
              <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations?.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" className="mt-6">
              Update
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !closures || closures.length === 0 ? (
            <div className="text-center py-16">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium">No Register Closures</h3>
              <p className="text-sm text-muted-foreground mt-1">
                No register sessions found for the selected filters
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Register</th>
                    <th className="text-left p-3 font-medium">#</th>
                    <th className="text-left p-3 font-medium">Time Opened</th>
                    <th className="text-left p-3 font-medium">Time Closed</th>
                    <th className="text-right p-3 font-medium">Cash Rounding</th>
                    <th className="text-right p-3 font-medium">Cash</th>
                    <th className="text-right p-3 font-medium">Variance</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {closures.map((closure) => {
                    const isExpanded = expandedRows.has(closure.id);
                    const variance = closure.variance ?? 0;
                    const hasVariance = Math.abs(variance) > 0.01;

                    return (
                      <Fragment key={closure.id}>
                        <tr
                          className={cn(
                            "border-b hover:bg-muted/30 cursor-pointer",
                            isExpanded && "bg-muted/20"
                          )}
                          onClick={() => toggleRow(closure.id)}
                        >
                          <td className="p-3 font-medium">
                            <div className="flex items-center gap-2">
                              {closure.session_type === 'virtual' && (
                                <Cloud className="h-4 w-4 text-blue-500" />
                              )}
                              {closure.register?.name || (closure.session_type === 'virtual' ? 'Virtual Session' : 'Unknown')}
                            </div>
                          </td>
                          <td className="p-3">
                            {closure.closure_number || '-'}
                          </td>
                          <td className="p-3">
                            <span className="text-primary hover:underline">
                              {new Date(closure.opened_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}, {new Date(closure.opened_at).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </span>
                          </td>
                          <td className="p-3">
                            {closure.closed_at ? (
                              new Date(closure.closed_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }) + ', ' + new Date(closure.closed_at).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })
                            ) : (
                              <Badge variant="secondary">Open</Badge>
                            )}
                          </td>
                          <td className="p-3 text-right">0.00</td>
                          <td className="p-3 text-right">
                            {formatCurrency(closure.cash_payments)}
                          </td>
                          <td className={cn(
                            "p-3 text-right font-medium",
                            hasVariance && variance > 0 && "text-green-600",
                            hasVariance && variance < 0 && "text-red-600"
                          )}>
                            {hasVariance ? (
                              variance > 0 ? `+${formatCurrency(variance)}` : formatCurrency(variance)
                            ) : '-'}
                          </td>
                          <td className="p-3">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </td>
                        </tr>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} className="p-0">
                              <div className="p-6 bg-muted/10 border-b">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                  {/* Session Info */}
                                  <div>
                                    <h4 className="font-medium mb-3">Session Details</h4>
                                    <dl className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Outlet</dt>
                                        <dd>{closure.location?.name || '-'}</dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Cashier</dt>
                                        <dd>
                                          {`${closure.user?.first_name || ''} ${closure.user?.last_name || ''}`.trim() || 'Unknown'}
                                        </dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Opening Balance</dt>
                                        <dd>{formatCurrency(closure.opening_balance)}</dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Expected Balance</dt>
                                        <dd>{closure.expected_balance ? formatCurrency(closure.expected_balance) : '-'}</dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Counted Balance</dt>
                                        <dd>{closure.closing_balance ? formatCurrency(closure.closing_balance) : '-'}</dd>
                                      </div>
                                    </dl>
                                  </div>

                                  {/* Cash Movements */}
                                  <div>
                                    <h4 className="font-medium mb-3">Cash Summary</h4>
                                    <dl className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Cash Payments</dt>
                                        <dd className="text-green-600">+{formatCurrency(closure.cash_payments)}</dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Cash Refunds</dt>
                                        <dd className="text-red-600">-{formatCurrency(closure.cash_refunds)}</dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Pay Ins</dt>
                                        <dd className="text-green-600">+{formatCurrency(closure.pay_ins)}</dd>
                                      </div>
                                      <div className="flex justify-between">
                                        <dt className="text-muted-foreground">Pay Outs</dt>
                                        <dd className="text-red-600">-{formatCurrency(closure.pay_outs)}</dd>
                                      </div>
                                    </dl>
                                  </div>

                                  {/* Items Sold Summary */}
                                  {closure.items_sold_summary && closure.items_sold_summary.length > 0 && (
                                    <div>
                                      <h4 className="font-medium mb-3">Items Sold</h4>
                                      <div className="max-h-40 overflow-y-auto text-sm space-y-1">
                                        {closure.items_sold_summary.slice(0, 10).map((item, i) => (
                                          <div key={i} className="flex justify-between">
                                            <span className="text-muted-foreground truncate mr-4">{item.name}</span>
                                            <span className="tabular-nums">{item.quantity}</span>
                                          </div>
                                        ))}
                                        {closure.items_sold_summary.length > 10 && (
                                          <div className="text-muted-foreground text-xs pt-1">
                                            +{closure.items_sold_summary.length - 10} more items
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Notes */}
                                {closure.notes && (
                                  <div className="mt-4 pt-4 border-t">
                                    <h4 className="font-medium mb-2">Notes</h4>
                                    <p className="text-sm text-muted-foreground">{closure.notes}</p>
                                  </div>
                                )}

                                {/* Settled By Info & Action Buttons */}
                                {closure.status === 'closed' && (
                                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                                    <div className="text-sm text-muted-foreground">
                                      {closure.settled_by_user && (
                                        <span>
                                          Settled by: {`${closure.settled_by_user.first_name || ''} ${closure.settled_by_user.last_name || ''}`.trim() || 'Unknown'}
                                          {closure.settled_at && (
                                            <> at {new Date(closure.settled_at).toLocaleString()}</>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                        onClick={(e) => handleViewSettlement(closure.id, e)}
                                      >
                                        <Eye className="h-4 w-4" />
                                        View Details
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                        onClick={(e) => handleReprintSettlement(closure.id, e)}
                                        disabled={reprintingId === closure.id}
                                      >
                                        {reprintingId === closure.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <RotateCcw className="h-4 w-4" />
                                        )}
                                        Reprint Settlement
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print template (hidden in UI) */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Register closures</h1>
            <div className="print-meta">
              Outlet: {selectedOutlet === 'all' ? 'All locations' : locations?.find(l => l.id === selectedOutlet)?.name || 'Unknown'} •
              Register: {selectedRegister === 'all' ? 'All registers' : registers?.find(r => r.id === selectedRegister)?.name || 'Unknown'}
            </div>
          </div>
          <div className="print-meta">Generated on {new Date().toLocaleString()}</div>
        </div>

        {closures?.length ? (
          <table className="print-table">
            <thead>
              <tr>
                <th>Register</th>
                <th>Closure #</th>
                <th>Outlet</th>
                <th>Opened</th>
                <th>Closed</th>
                <th>Opening balance</th>
                <th>Expected</th>
                <th>Counted</th>
                <th>Variance</th>
                <th>Cashier</th>
              </tr>
            </thead>
            <tbody>
              {closures.map((c) => (
                <tr key={c.id}>
                  <td>{c.register?.name || 'Unknown'}</td>
                  <td>{c.closure_number || '-'}</td>
                  <td>{c.location?.name || 'Unknown'}</td>
                  <td>{new Date(c.opened_at).toLocaleString()}</td>
                  <td>{c.closed_at ? new Date(c.closed_at).toLocaleString() : 'Open'}</td>
                  <td>{formatCurrency(c.opening_balance)}</td>
                  <td>{c.expected_balance !== null ? formatCurrency(c.expected_balance) : '-'}</td>
                  <td>{c.closing_balance !== null ? formatCurrency(c.closing_balance) : '-'}</td>
                  <td>{c.variance !== null ? formatCurrency(c.variance) : '-'}</td>
                  <td>{`${c.user?.first_name || ''} ${c.user?.last_name || ''}`.trim() || 'Unknown'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="print-meta">No register closures found.</div>
        )}
      </div>
    </div>
  );
}
